import { useCallback, useEffect, useMemo, useState } from 'react';

type IssueList = { issueKey: string; url: string; readonly?: boolean }[];

type SelectedIssuesState = Record<string, { url: string; isSelected: boolean; readonly?: boolean }>;

type UseSelectedIssues = {
	issues: SelectedIssuesState;
	selectedIssueKeys: string[];
	areAllIssuesSelected: (disabledIssues: string[]) => boolean;
	toggleIssue: (issueKey: string) => void;
	toggleIssues: (issueKeys: string[], newCheckboxState: boolean) => void;
};

/**
 * Hook for managing selected state of issues displayed in the issue selector.
 * @param initialIssueList - the initial list of issues, will be converted to a map with `isSelected` defaulting to `false`
 */
export const useSelectedIssues = (
	issueList: IssueList,
	initialSelectedIssues?: string[],
): UseSelectedIssues => {
	const [issues, setIssues] = useState<SelectedIssuesState>(() => {
		const initialSelectedSet = new Set(initialSelectedIssues || []);
		return Object.fromEntries(
			issueList.map(({ issueKey, url, readonly }) => [
				issueKey,
				{ isSelected: initialSelectedSet.has(issueKey), url, readonly },
			]),
		);
	});

	useEffect(() => {
		const hasChanges =
			issueList.some(({ issueKey, readonly }) => {
				const currentIssue = issues[issueKey];
				return !currentIssue || currentIssue.readonly !== readonly;
			}) || Object.keys(issues).length !== issueList.length;

		if (hasChanges) {
			setIssues((prevIssues) =>
				Object.fromEntries(
					issueList.map(({ issueKey, url, readonly }) => [
						issueKey,
						{
							isSelected: prevIssues[issueKey]?.isSelected ?? false,
							url,
							readonly,
						},
					]),
				),
			);
		}
	}, [issueList, setIssues, issues]);

	const selectedIssueKeys = useMemo(
		() => Object.keys(issues).filter((issueKey) => issues[issueKey].isSelected),
		[issues],
	);

	const areAllIssuesSelected = useCallback(
		(disabledIssues: string[]) => {
			if (disabledIssues.length) {
				return (
					selectedIssueKeys.length ===
					Object.keys(issues).filter((issueKey) => !disabledIssues.includes(issueKey)).length
				);
			}
			return selectedIssueKeys.length === Object.keys(issues).length;
		},
		[selectedIssueKeys, issues],
	);

	const toggleIssue = useCallback((issueKey: string) => {
		setIssues((prevIssues) => ({
			...prevIssues,
			[issueKey]: {
				...prevIssues[issueKey],
				isSelected: !prevIssues[issueKey].isSelected,
			},
		}));
	}, []);

	const toggleIssues = useCallback((issueKeys: string[], newCheckboxState: boolean) => {
		setIssues((prevIssues) => {
			const newIssues = { ...prevIssues };
			issueKeys.forEach((issueKey) => {
				if (newIssues[issueKey]) {
					newIssues[issueKey].isSelected = newCheckboxState;
				}
			});
			return newIssues;
		});
	}, []);

	return {
		issues,
		selectedIssueKeys,
		areAllIssuesSelected,
		toggleIssue,
		toggleIssues,
	};
};
